import { useEffect } from 'react';

import { fireAnalytics } from '@hooks/useAnalytics/useAnalytics';
import type { ShoppingCartDTO } from '@t/api.types';
import type { OrderAnalyticsQualification } from '@utils/orderAnalyticsQualification/orderAnalyticsQualification';

// "Page viewed" is the canonical imperative-library-API trigger (per
// .claude/rules/react.md): synchronise an external system (window.dataLayer)
// with the moment the visitor enters the order funnel. Entering the funnel is
// three things at once — a session, a resolved cart, and an address check that
// qualified the visitor onto the product page. Data-readiness alone is not
// entry: session and cart both self-satisfy with zero user input, so a
// readiness-only guard reported every visitor still staring at the Check
// Availability dialog (SC-13175). Containment holds because `fireAnalytics`
// owns the env guard + dynamic import for the analytics subgraph.
//
// Keep this hook pure-input — it must not read the qualification store, or the
// `renderHook` seam is lost. `toOrderAnalyticsQualification` is the adapter that
// turns store state into the `null`-means-do-not-report input below.
export const useTrackOrderLandingPageView = (input: {
  sessionId: string | null;
  cart: Pick<ShoppingCartDTO, 'shoppingCartId'> | null | undefined;
  /** `null` = the visitor has not (yet) entered the order funnel; do not report. */
  qualification: OrderAnalyticsQualification | null;
}): void => {
  const { sessionId } = input;
  const cartId = input.cart?.shoppingCartId;
  // Destructured to primitives on purpose — do not inline `input.qualification`
  // into the dependency array. The object is rebuilt every render (it would
  // refire on every render), while the ids must stay in the array because
  // refiring on a change of address is the specced contract, not an accident.
  const hasQualification = input.qualification !== null;
  const { qualifiedAddressId = null, resolvedAddressId = null } = input.qualification ?? {};

  useEffect(() => {
    if (!sessionId || cartId === undefined || !hasQualification) return;
    fireAnalytics('trackOrderLandingPageViewed', {
      sessionId,
      cart: { shoppingCartId: cartId },
      qualification: { qualifiedAddressId, resolvedAddressId },
    });
  }, [sessionId, cartId, hasQualification, qualifiedAddressId, resolvedAddressId]);
};
